import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import slugify from 'common/util/slugify';
import makeFullName from 'common/util/makeFullName';

import {
  isVerificationOfRentalRequired,
  isVerificationOfEmploymentCompleted,
  isVerificationOfRentalCompleted,
} from 'common/householdApplication/applicationHelpers';

import { SR_API_ACTION_HOUSEHOLD_UPDATE_APPLICATION } from 'api/actionTypes';

import { INCOME_VERIFICATION_STATUS_COMPLETED } from 'common/incomeVerificationConstants';

import {
  VERIFICATION_OF_RENTAL_STATUS_OWN,
  VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY,
} from 'common/verificationOfRentalConstants';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
} from 'common/applicationAssemblyLineConstants';

import useBranch from 'client/hooks/useBranch';
import postWithState from 'client/actions/postWithState';

import { makeProxiedS3FilePath } from 'client/lib/fileUploader';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from 'client/components/SrButton/SrButton';
import SrFormikField from 'client/components/SrFormikField';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';
import { showFieldIf } from 'client/helpers/formikHelpers';

export const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE = ' SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE';
export const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR = ' SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR';

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_LINK = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'VOE',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'VOR',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_TITLE = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'verification of employment',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'verification of rental',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ENV_VAR_TYPEFORM_ID = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'TYPEFORM_ID_VERIFICATION_OF_EMPLOYMENT',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'TYPEFORM_ID_VERIFICATION_OF_RENTAL',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_FORM_ITEMS = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'verificationOfEmploymentTypeformResponse',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'verificationOfRentalTypeformResponse',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ADDITIONAL_DOCS_FIELD_ID = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'verificationOfEmploymentAdditionalDocuments',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'verificationOfRentalAdditionalDocuments',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_VALIDATOR = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: isVerificationOfEmploymentCompleted,
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: isVerificationOfRentalCompleted,
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_NULLIFY_FIELDS = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: [
    'verificationOfEmploymentPdfUrl',
    'verificationOfEmploymentPdfCreatedDate',
    'verificationOfEmploymentTypeformResponse',
  ],
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: [
    'verificationOfRentalPdfUrl',
    'verificationOfRentalPdfCreatedDate',
    'verificationOfRentalTypeformResponse',
  ],
};

const SR_VOR_STATUS_TO_NO_VOR_TEXT_REASON = {
  [VERIFICATION_OF_RENTAL_STATUS_OWN]: 'VOR Not Needed, applicant is a homeowner. ',
  [VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY]: 'VOR Not Needed, applicant has no rental history. ',
};

const getFormFields = ({ typeformId, renterProfile }) => {
  const formFieldId = SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ADDITIONAL_DOCS_FIELD_ID[typeformId];
  const formValue = renterProfile[formFieldId] || [];
  const userNameSlug = slugify(makeFullName(renterProfile.firstName, renterProfile.lastName));
  const isHomeowner = renterProfile.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_OWN;
  const noRentalHistory = renterProfile.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY;

  const showVerificationOfRentalStatus =
    typeformId === SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR && (isHomeowner || noRentalHistory);

  return [
    showFieldIf(showVerificationOfRentalStatus, {
      id: 'verificationOfRentalStatus',
      label: 'Status',
      type: 'select',
      properties: {
        choices: [
          {
            label: 'Not Qualified',
            value: SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
          },
          {
            label: 'Qualified',
            value: SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
          },
        ],
      },
    }),
    {
      id: formFieldId,
      label: 'Additional documents',
      type: 'file_multi',
      properties: {
        fileAccept: '*',
        filePath: `/renter-application-uploads/${userNameSlug}-${slugify(
          SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_LINK[typeformId],
        )}-${formValue.length + 1}-{uuid}`, // eslint-disable-line max-len
        isPrivate: true,
        uploadAs: renterProfile.userId,
      },
    },
  ];
};

const SrHouseholdApplicationTypeform = ({
  typeformId,
  renterProfile,
  dispatch,
  onUpdateHousehold,
  incomeVerification,
  employmentVerification,
  acceptBankDataAsVoe,
  tenantAlertReport,
  tenantScreeningCriteria,
}) => {
  const [isModalOpen, setModalIsOpen] = useState(false);
  const [isRemovingProofOfHomeOwnership, setIsRemovingProofOfHomeOwnership] = useState(false);
  const { TYPEFORM_ID, appUrl } = useBranch({
    TYPEFORM_ID: ['env', SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ENV_VAR_TYPEFORM_ID[typeformId]],
    appUrl: ['env', 'APP_URL'],
  });
  const typeformUrl = `https://sunroom.typeform.com/to/${TYPEFORM_ID}?user_id=${renterProfile.userId}`;
  const typeformAnswers =
    renterProfile[SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_FORM_ITEMS[typeformId]] || [];
  const formFieldId = SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ADDITIONAL_DOCS_FIELD_ID[typeformId];
  const formFields = getFormFields({
    typeformId,
    renterProfile,
  });

  const onToggleModal = () => {
    setModalIsOpen(!isModalOpen);
  };

  const onSubmit = (formValues) => {
    const additionalDocuments = formValues[formFieldId];
    const updates = {
      [formFieldId]: additionalDocuments,
    };

    if (formValues.verificationOfRentalStatus) {
      updates.verificationOfRentalStatus = formValues.verificationOfRentalStatus;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_UPDATE_APPLICATION,
      queryId: formFieldId,
      payload: {
        userId: renterProfile.userId,
        updates,
      },
    }).then(() => {
      onUpdateHousehold();
      setModalIsOpen(false);
    });
  };

  const onRemoveProofOfHomeOwnership = () => {
    setIsRemovingProofOfHomeOwnership(true);
    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_UPDATE_APPLICATION,
      queryId: formFieldId,
      payload: {
        userId: renterProfile.userId,
        updates: {
          proofOfHomeOwnershipUrl: null,
        },
      },
    }).then(() => {
      onUpdateHousehold();
    });
  };

  const onRemoveTypeformAnswers = () => {
    if (!global.confirm('Are you sure you want to remove the answers?')) {
      return;
    }

    const fieldsToNullify = SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_NULLIFY_FIELDS[
      typeformId
    ].reduce(
      (memo, fieldName) => ({
        ...memo,
        [fieldName]: null,
      }),
      {},
    );

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_UPDATE_APPLICATION,
      queryId: 'removeTypeformAnswers',
      payload: {
        userId: renterProfile.userId,
        updates: fieldsToNullify,
      },
    }).then(() => {
      onUpdateHousehold();
      setModalIsOpen(false);
    });
  };

  const shouldShowIncomeVerificationBody =
    acceptBankDataAsVoe &&
    incomeVerification &&
    incomeVerification?.status === INCOME_VERIFICATION_STATUS_COMPLETED &&
    incomeVerification.incomeMonthly > 0;

  const shouldDisplayVoeReport =
    shouldShowIncomeVerificationBody &&
    employmentVerification &&
    employmentVerification?.reportFileUrl &&
    employmentVerification?.isReportValid;

  const verificationOfRentalRequired =
    typeformId === SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR && tenantScreeningCriteria
      ? isVerificationOfRentalRequired({
          tenantScreeningCriteria,
          tenantAlertReport,
        })
      : true;

  const isHomeowner =
    renterProfile.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_OWN &&
    typeformId === SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR;

  const vorNoRentalHistory =
    renterProfile.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY &&
    typeformId === SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR;

  const completed =
    typeformId === SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE
      ? SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_VALIDATOR[typeformId]({
          householdApplication: renterProfile,
          partner: {
            acceptBankDataAsVoe,
          },
        })
      : tenantScreeningCriteria
      ? SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_VALIDATOR[typeformId]({
          householdApplication: renterProfile,
          tenantAlertReport,
          tenantScreeningCriteria,
        })
      : false;

  const opacity =
    completed ||
    shouldShowIncomeVerificationBody ||
    !verificationOfRentalRequired ||
    isHomeowner ||
    vorNoRentalHistory
      ? '1'
      : '0.25';

  return (
    <SrText padding="0 4px">
      <SrText
        color="blue300"
        weight="500"
        cursor="pointer"
        onClick={renterProfile.userId ? onToggleModal : null}
        opacity={opacity}
      >
        {SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_LINK[typeformId]}
      </SrText>
      {isModalOpen && (
        <SrDarkModal>
          <SrModalContent style={{ minWidth: 700, marginTop: 50 }} onCloseIconClick={onToggleModal}>
            <SrText font="whitney-sc" weight="800" size="23" align="center" display="block">
              {SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_TITLE[typeformId]}
            </SrText>
            <SrBox marginTop="20px">
              {shouldDisplayVoeReport ||
              !verificationOfRentalRequired ||
              isHomeowner ||
              vorNoRentalHistory ? (
                <SrBox>
                  <SrText font="greycliff" weight="400" fontSize="17px" lineHeight="28px">
                    {shouldDisplayVoeReport
                      ? 'No verification of employment needed. Income proven using Pinwheel.'
                      : !verificationOfRentalRequired
                      ? 'VOR is not required for this listing.'
                      : SR_VOR_STATUS_TO_NO_VOR_TEXT_REASON[renterProfile.rentalStatus]}
                  </SrText>
                  {shouldDisplayVoeReport && (
                    <SrBox padding="20px 0">
                      <SrS3FileDisplay
                        url={employmentVerification.reportFileUrl}
                        maxHeight="600px"
                      />
                    </SrBox>
                  )}
                  {vorNoRentalHistory && (
                    <SrBox>
                      <SrText font="greycliff" weight="400" fontSize="17px" lineHeight="28px">
                        Rental History Description:
                        <SrText font="greycliff" weight="600" fontSize="17px" lineHeight="28px">
                          {` ${renterProfile.rentalStatusExplanation} `}
                        </SrText>
                      </SrText>
                    </SrBox>
                  )}
                  {renterProfile.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_OWN &&
                    renterProfile.proofOfHomeOwnershipUrl && (
                      <SrBox padding="20px 0">
                        <SrS3FileDisplay
                          url={makeProxiedS3FilePath(renterProfile.proofOfHomeOwnershipUrl, appUrl)}
                          maxHeight="600px"
                        />
                        <SrText
                          display="block"
                          align="center"
                          color="blue300"
                          cursor="pointer"
                          opacity={isRemovingProofOfHomeOwnership ? '0.25' : '1'}
                          onClick={
                            isRemovingProofOfHomeOwnership ? null : onRemoveProofOfHomeOwnership
                          }
                        >
                          Remove document
                        </SrText>
                      </SrBox>
                    )}
                </SrBox>
              ) : (
                <TypeformBody
                  typeformUrl={typeformUrl}
                  typeformAnswers={typeformAnswers}
                  onRemoveTypeformAnswers={onRemoveTypeformAnswers}
                />
              )}
              <SrBox marginTop="20px" />
              <Formik
                enableReinitialize
                initialValues={{
                  verificationOfRentalAdditionalDocuments:
                    renterProfile.verificationOfRentalAdditionalDocuments || [],
                  verificationOfEmploymentAdditionalDocuments:
                    renterProfile.verificationOfEmploymentAdditionalDocuments || [],
                  verificationOfRentalStatus: renterProfile.verificationOfRentalStatus || '',
                }}
                onSubmit={onSubmit}
                render={(formikBag) => {
                  return (
                    <Form>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                        />
                      ))}
                      <SrButton
                        type="submit"
                        theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                        disabled={!formikBag.isValid}
                      >
                        Save
                      </SrButton>
                    </Form>
                  );
                }}
              />
            </SrBox>
          </SrModalContent>
        </SrDarkModal>
      )}
    </SrText>
  );
};

const TypeformBody = ({ typeformUrl, typeformAnswers, onRemoveTypeformAnswers }) => {
  return (
    <>
      <SrText font="whitney-sc" weight="500" color="blue400" display="block">
        typeform link
      </SrText>
      <SrLink to={typeformUrl} target="_blank">
        <SrText color="blue300">{typeformUrl}</SrText>
      </SrLink>
      <SrBox margin="20px 0">
        <SrText font="whitney-sc" weight="500" color="blue400" display="block">
          typeform answers
        </SrText>
        {typeformAnswers.map(({ id, title, value }, i) => {
          return (
            <SrBox key={id} marginTop="5px">
              <SrText weight="500" display="block">
                {i + 1}. {title}
              </SrText>
              <SrText paddingLeft="10px">{value}</SrText>
            </SrBox>
          );
        })}
        {typeformAnswers.length === 0 && (
          <SrBox marginTop="5px">
            <SrText>--</SrText>
          </SrBox>
        )}
        {typeformAnswers.length > 0 && (
          <SrBox marginTop="5px">
            <SrButton onClick={onRemoveTypeformAnswers} theme={SR_BUTTON_THEME_ADOBE_BLUE}>
              Remove answers
            </SrButton>
          </SrBox>
        )}
      </SrBox>
    </>
  );
};

SrHouseholdApplicationTypeform.propTypes = {
  typeformId: PropTypes.oneOf([
    SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE,
    SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR,
  ]).isRequired,
  renterProfile: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  onUpdateHousehold: PropTypes.func.isRequired,
  incomeVerification: PropTypes.object,
  employmentVerification: PropTypes.object,
  acceptBankDataAsVoe: PropTypes.bool,
};

SrHouseholdApplicationTypeform.displayName = 'SrHouseholdApplicationTypeform';

export default SrHouseholdApplicationTypeform;
